import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useAuth } from "react-oidc-context";
import { useNavigate } from "react-router-dom";
import type { CognitoUserSession } from "amazon-cognito-identity-js";

import { currentSession, signOutLocal } from "@/auth/cognito/client";
import { authMode, type AuthMode } from "@/auth/cognito/pool";
import { markFreshLogin, SITE_URL, signOutRedirect } from "@/auth/oidcConfig";
import {
  clearFederatedRetry,
  decideFederatedRetry,
  FED_ATTEMPT_KEY,
  FED_PROVIDER_KEY,
  ssGet,
  ssSet,
} from "./federatedRetry";

/**
 * The one place the rest of the app asks "who is signed in?".
 *
 * <h2>Why a facade at all</h2>
 * There are now two ways to hold a session and they cannot be collapsed:
 *
 * <ul>
 *   <li><b>Native</b> — our own sign-in pages exchange an email + password with
 *       Cognito over SRP. No redirect, no page we don't own.</li>
 *   <li><b>Federated / redirect</b> — "Continue with Google" (and the whole of
 *       Keycloak-backed local dev) must leave the origin, because that's what
 *       the authorization-code flow <i>is</i>. react-oidc-context owns it.</li>
 * </ul>
 *
 * Components shouldn't have to care which one produced the session, so this
 * merges them and exposes a single shape. A federated session wins when both
 * exist (it is by definition the more recent act of signing in).
 *
 * <h2>Which token the API gets</h2>
 * The <b>ID token</b> for Cognito, the access token for anything else, exposed
 * as {@link Session#apiToken}. This is not cosmetic: a Cognito access token
 * minted by a direct SRP sign-in carries the scope
 * {@code aws.cognito.signin.user.admin} rather than {@code openid email
 * profile}, and it has no profile claims at all. The backend's
 * {@code OidcUserInfoClient} would get a 401 from the userinfo endpoint and
 * {@code CurrentUserService} would then provision the account as
 * {@code <sub>@unknown.local}. The ID token carries {@code email},
 * {@code given_name} and {@code family_name} directly, so the backend reads
 * them off the token and skips the round trip entirely.
 */
export type SessionUser = {
  /** JWT to send to our API — see the note above on why this is the ID token. */
  apiToken: string;
  idToken: string;
  accessToken: string;
  profile: {
    sub?: string;
    email?: string;
    given_name?: string;
    family_name?: string;
    name?: string;
  };
};

export type Session = {
  mode: AuthMode;
  isLoading: boolean;
  isAuthenticated: boolean;
  error: Error | null;
  user: SessionUser | null;
  /** Send the user to sign in: our own page natively, the IdP otherwise. */
  signIn: () => void;
  /** Skip straight to Google — Cognito's own pages never render. */
  signInWithGoogle: () => void;
  signOut: () => void;
  /** Adopt a session just produced by the native pages. */
  adoptCognitoSession: (session: CognitoUserSession, opts?: { freshLogin?: boolean }) => void;
  /** Re-read (and if needed refresh) the session. False = no session left. */
  refresh: () => Promise<boolean>;
};

const SessionContext = createContext<Session | null>(null);

/** Reads the profile claims straight off the ID token. */
function toUser(session: CognitoUserSession): SessionUser {
  const idToken = session.getIdToken();
  return {
    apiToken: idToken.getJwtToken(),
    idToken: idToken.getJwtToken(),
    accessToken: session.getAccessToken().getJwtToken(),
    profile: idToken.decodePayload() as SessionUser["profile"],
  };
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const oidc = useAuth();
  const navigate = useNavigate();
  const mode = useMemo(() => authMode(), []);

  const [nativeUser, setNativeUser] = useState<SessionUser | null>(null);
  // Only the native path has a session to restore; in oidc mode we're never
  // waiting on one, so don't hold the app back for it.
  const [restoring, setRestoring] = useState(mode === "cognito");

  // Restore a cached native session on first mount. The SDK persists it to
  // local storage and refreshes it via the refresh token when the access token
  // has expired, so this covers both "reload the tab" and "come back tomorrow".
  //
  // Guarded only by `cancelled`, deliberately: a "have we run yet" ref would
  // deadlock under StrictMode, whose mount → unmount → remount cycle cancels the
  // first run and then finds the ref already set, leaving `restoring` true
  // forever and the app stuck on its loading state. Reading the cached session
  // twice is free.
  useEffect(() => {
    if (mode !== "cognito") return;
    let cancelled = false;
    void currentSession()
      .then((session) => {
        if (cancelled) return;
        setNativeUser(session ? toUser(session) : null);
      })
      .finally(() => {
        if (!cancelled) setRestoring(false);
      });
    return () => {
      cancelled = true;
    };
  }, [mode]);

  // Transparent one-shot retry for the first federated sign-in (see
  // federatedRetry). Only ever acts when signInWithGoogle armed the marker, so
  // the native and Keycloak paths are untouched.
  useEffect(() => {
    const provider = ssGet(FED_PROVIDER_KEY);
    const action = decideFederatedRetry({
      provider,
      isAuthenticated: oidc.isAuthenticated,
      hasError: Boolean(oidc.error),
      errorDetail: (oidc.error as { error?: string })?.error ?? oidc.error?.message ?? "",
      attempts: Number(ssGet(FED_ATTEMPT_KEY) ?? "0"),
    });
    if (action === "clear") {
      clearFederatedRetry();
    } else if (action === "retry" && provider) {
      ssSet(FED_ATTEMPT_KEY, String(Number(ssGet(FED_ATTEMPT_KEY) ?? "0") + 1));
      void oidc.signinRedirect({ extraQueryParams: { identity_provider: provider } });
    }
  }, [oidc.error, oidc.isAuthenticated, oidc]);

  const adoptCognitoSession = useCallback(
    (session: CognitoUserSession, opts?: { freshLogin?: boolean }) => {
      if (opts?.freshLogin !== false) markFreshLogin();
      setNativeUser(toUser(session));
      setRestoring(false);
    },
    [],
  );

  // A federated session outranks a native one: it is the more recent sign-in.
  const oidcUser: SessionUser | null = oidc.isAuthenticated && oidc.user
    ? {
        apiToken: oidc.user.id_token ?? oidc.user.access_token,
        idToken: oidc.user.id_token ?? "",
        accessToken: oidc.user.access_token,
        profile: oidc.user.profile as SessionUser["profile"],
      }
    : null;

  // Renew whichever kind of session we hold. Callers (TokenSync, on a 401) get
  // one method and don't have to know which path produced the token.
  const holdsOidcSession = oidcUser !== null;
  const refresh = useCallback(async () => {
    if (holdsOidcSession) {
      // Redirect-flow session: refresh_token grant via the OIDC library.
      return (await oidc.signinSilent()) != null;
    }
    if (mode !== "cognito") return false;
    const session = await currentSession();
    setNativeUser(session ? toUser(session) : null);
    return session !== null;
  }, [holdsOidcSession, mode, oidc]);

  const signIn = useCallback(() => {
    if (mode === "cognito") navigate("/signin");
    else void oidc.signinRedirect();
  }, [mode, navigate, oidc]);

  const signInWithGoogle = useCallback(() => {
    // Arm the one-shot retry (see the module note) before leaving for Google.
    ssSet(FED_PROVIDER_KEY, "Google");
    ssSet(FED_ATTEMPT_KEY, "0");
    // identity_provider makes Cognito forward straight to Google instead of
    // rendering its own provider-picker page.
    void oidc.signinRedirect({ extraQueryParams: { identity_provider: "Google" } });
  }, [oidc]);

  const signOut = useCallback(() => {
    if (oidcUser) {
      // Bounce through the IdP so its own session dies too, otherwise the next
      // sign-in silently re-authenticates.
      void signOutRedirect(oidc);
      return;
    }
    // A native session never created a session cookie at the IdP — there is
    // nothing server-side to tear down, so dropping the cached tokens IS the
    // sign-out. (The refresh token stays valid until it expires but is no
    // longer held anywhere.)
    signOutLocal();
    setNativeUser(null);
    window.location.assign(SITE_URL);
  }, [oidc, oidcUser]);

  const value = useMemo<Session>(
    () => ({
      mode,
      isLoading: oidc.isLoading || restoring,
      isAuthenticated: Boolean(oidcUser ?? nativeUser),
      error: oidc.error ?? null,
      user: oidcUser ?? nativeUser,
      signIn,
      signInWithGoogle,
      signOut,
      adoptCognitoSession,
      refresh,
    }),
    [
      mode,
      oidc.isLoading,
      oidc.error,
      oidcUser,
      nativeUser,
      restoring,
      signIn,
      signInWithGoogle,
      signOut,
      adoptCognitoSession,
      refresh,
    ],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): Session {
  const ctx = useContext(SessionContext);
  if (!ctx) {
    throw new Error("useSession must be used inside <SessionProvider>");
  }
  return ctx;
}
